const DRAWING_PDF_CACHE_DB = 'forxa-drawing-pdf-cache';
const DRAWING_PDF_CACHE_STORE = 'drawing-pdfs';
const DRAWING_PDF_CACHE_VERSION = 1;
const DRAWING_PDF_CACHE_TTL_MS = 24 * 60 * 60 * 1_000;
const DRAWING_PDF_CACHE_MAX_BYTES = 150 * 1024 * 1024;
const DRAWING_PDF_CACHE_MAX_ENTRIES = 10;
const DRAWING_PDF_CACHE_MAX_FILE_BYTES = 64 * 1024 * 1024;
const DRAWING_PDF_DIRECT_CHUNK_BYTES = 4 * 1024 * 1024;
const DRAWING_PDF_PROXY_CHUNK_BYTES = 2 * 1024 * 1024;
const DRAWING_PDF_DIRECT_CONCURRENCY = 3;
const DRAWING_PDF_CACHE_CHANNEL = 'forxa-drawing-pdf-cache-control';

interface DrawingPdfCacheEntry {
  key: string;
  scope: string;
  identity: string;
  bytes: ArrayBuffer;
  size: number;
  createdAt: number;
  lastAccessedAt: number;
}

interface DrawingPdfCacheRequest {
  scope: string;
  identity: string;
  url: string;
  expectedSize?: number;
  forceRefresh?: boolean;
  signal?: AbortSignal;
}

let databasePromise: Promise<IDBDatabase> | null = null;
const inFlightLoads = new Map<string, Promise<Uint8Array>>();
const inFlightAbortControllers = new Map<string, AbortController>();
let cacheGeneration = 0;
let cacheChannel: BroadcastChannel | null = null;

function ensureCacheChannel(): BroadcastChannel | null {
  if (cacheChannel || typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') return cacheChannel;
  cacheChannel = new window.BroadcastChannel(DRAWING_PDF_CACHE_CHANNEL);
  cacheChannel.onmessage = (event) => {
    if (event.data?.type === 'clear') void clearDrawingPdfCacheInternal(false).catch(() => {});
  };
  return cacheChannel;
}

function cacheKey(scope: string, identity: string): string {
  return `${scope}:${identity}`;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Drawing cache request failed'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('Drawing cache transaction was aborted'));
    transaction.onerror = () => reject(transaction.error || new Error('Drawing cache transaction failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  ensureCacheChannel();
  databasePromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DRAWING_PDF_CACHE_DB, DRAWING_PDF_CACHE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DRAWING_PDF_CACHE_STORE)) {
        const store = database.createObjectStore(DRAWING_PDF_CACHE_STORE, { keyPath: 'key' });
        store.createIndex('scope', 'scope', { unique: false });
        store.createIndex('lastAccessedAt', 'lastAccessedAt', { unique: false });
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => {
        request.result.close();
        databasePromise = null;
      };
      resolve(request.result);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Drawing cache is unavailable'));
    };
    request.onblocked = () => {
      databasePromise = null;
      reject(new Error('Drawing cache upgrade is blocked'));
    };
  });
  return databasePromise;
}

async function deleteEntry(key: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(DRAWING_PDF_CACHE_STORE, 'readwrite');
  transaction.objectStore(DRAWING_PDF_CACHE_STORE).delete(key);
  await transactionComplete(transaction);
}

async function readEntry(scope: string, identity: string): Promise<Uint8Array | null> {
  const database = await openDatabase();
  const key = cacheKey(scope, identity);
  const transaction = database.transaction(DRAWING_PDF_CACHE_STORE, 'readonly');
  const entry = await requestResult(
    transaction.objectStore(DRAWING_PDF_CACHE_STORE).get(key),
  ) as DrawingPdfCacheEntry | undefined;
  await transactionComplete(transaction);
  if (!entry) return null;
  if (Date.now() - entry.createdAt > DRAWING_PDF_CACHE_TTL_MS) {
    await deleteEntry(key);
    return null;
  }

  // Return a copy because PDF.js may transfer/detach the provided buffer when
  // it hands the document to its worker.
  const bytes = new Uint8Array(entry.bytes.slice(0));
  const update = database.transaction(DRAWING_PDF_CACHE_STORE, 'readwrite');
  update.objectStore(DRAWING_PDF_CACHE_STORE).put({ ...entry, lastAccessedAt: Date.now() });
  await transactionComplete(update).catch(() => {});
  return bytes;
}

async function pruneCache(activeScope: string): Promise<void> {
  const database = await openDatabase();
  const read = database.transaction(DRAWING_PDF_CACHE_STORE, 'readonly');
  const entries = await requestResult(
    read.objectStore(DRAWING_PDF_CACHE_STORE).getAll(),
  ) as DrawingPdfCacheEntry[];
  await transactionComplete(read);

  const now = Date.now();
  const activeEntries = entries
    .filter((entry) => entry.scope === activeScope && now - entry.createdAt <= DRAWING_PDF_CACHE_TTL_MS)
    .sort((a, b) => b.lastAccessedAt - a.lastAccessedAt);
  let retainedBytes = 0;
  const retainedKeys = new Set<string>();
  for (const entry of activeEntries) {
    if (retainedKeys.size >= DRAWING_PDF_CACHE_MAX_ENTRIES) continue;
    if (retainedBytes + entry.size > DRAWING_PDF_CACHE_MAX_BYTES) continue;
    retainedKeys.add(entry.key);
    retainedBytes += entry.size;
  }

  const keysToDelete = entries
    .filter((entry) => entry.scope !== activeScope || !retainedKeys.has(entry.key))
    .map((entry) => entry.key);
  if (!keysToDelete.length) return;
  const cleanup = database.transaction(DRAWING_PDF_CACHE_STORE, 'readwrite');
  const store = cleanup.objectStore(DRAWING_PDF_CACHE_STORE);
  keysToDelete.forEach((key) => store.delete(key));
  await transactionComplete(cleanup);
}

async function storeEntry(scope: string, identity: string, bytes: ArrayBuffer, generation: number): Promise<void> {
  if (bytes.byteLength > DRAWING_PDF_CACHE_MAX_FILE_BYTES) return;
  if (generation !== cacheGeneration) return;
  const database = await openDatabase();
  if (generation !== cacheGeneration) return;
  const now = Date.now();
  const entry: DrawingPdfCacheEntry = {
    key: cacheKey(scope, identity),
    scope,
    identity,
    bytes,
    size: bytes.byteLength,
    createdAt: now,
    lastAccessedAt: now,
  };
  const transaction = database.transaction(DRAWING_PDF_CACHE_STORE, 'readwrite');
  transaction.objectStore(DRAWING_PDF_CACHE_STORE).put(entry);
  await transactionComplete(transaction);
  await pruneCache(scope);
}

function isSameOriginUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return true;
  }
}

async function fetchPdfRange(url: string, start: number, end: number, signal?: AbortSignal): Promise<Uint8Array> {
  const response = await fetch(url, {
    credentials: isSameOriginUrl(url) ? 'include' : 'omit',
    signal,
    headers: {
      Accept: 'application/pdf',
      Range: `bytes=${start}-${end}`,
    },
  });
  if (response.status !== 206) {
    throw new Error(`Could not load drawing PDF range (${response.status})`);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  const expectedLength = end - start + 1;
  const contentRange = response.headers.get('content-range');
  if (contentRange && !contentRange.startsWith(`bytes ${start}-${end}/`)) {
    throw new Error('The drawing PDF range metadata was invalid');
  }
  if (bytes.byteLength !== expectedLength) {
    throw new Error('The drawing PDF range was incomplete');
  }
  return bytes;
}

async function fetchPdfBytes(url: string, expectedSize?: number, signal?: AbortSignal): Promise<ArrayBuffer> {
  const direct = !isSameOriginUrl(url);
  const chunkBytes = direct ? DRAWING_PDF_DIRECT_CHUNK_BYTES : DRAWING_PDF_PROXY_CHUNK_BYTES;
  const concurrency = direct ? DRAWING_PDF_DIRECT_CONCURRENCY : 1;
  if (expectedSize && expectedSize > chunkBytes) {
    const ranges: Array<{ start: number; end: number }> = [];
    for (let start = 0; start < expectedSize; start += chunkBytes) {
      ranges.push({
        start,
        end: Math.min(expectedSize - 1, start + chunkBytes - 1),
      });
    }
    const combined = new Uint8Array(expectedSize);
    let nextRange = 0;
    const worker = async () => {
      while (nextRange < ranges.length) {
        const index = nextRange;
        nextRange += 1;
        const range = ranges[index];
        const bytes = await fetchPdfRange(url, range.start, range.end, signal);
        combined.set(bytes, range.start);
      }
    };
    await Promise.all(
      Array.from(
        { length: Math.min(concurrency, ranges.length) },
        () => worker(),
      ),
    );
    return combined.buffer;
  }

  const response = await fetch(url, {
    credentials: direct ? 'omit' : 'include',
    signal,
    headers: { Accept: 'application/pdf' },
  });
  if (!response.ok) throw new Error(`Could not load drawing PDF (${response.status})`);
  const buffer = await response.arrayBuffer();
  if (expectedSize && buffer.byteLength !== expectedSize) {
    throw new Error('The drawing PDF download was incomplete');
  }
  return buffer;
}

async function downloadPdf({ scope, identity, url, expectedSize, forceRefresh, signal }: DrawingPdfCacheRequest): Promise<Uint8Array> {
  const generation = cacheGeneration;
  if (forceRefresh) {
    await deleteEntry(cacheKey(scope, identity)).catch(() => {});
  } else {
    const cached = await readEntry(scope, identity).catch(() => null);
    if (cached) return cached;
  }

  const buffer = await fetchPdfBytes(url, expectedSize, signal);
  if (!buffer.byteLength) throw new Error('The drawing PDF is empty');

  // Store the untouched server response. The copy passed to PDF.js is kept
  // separate so worker transfer cannot corrupt the persistent cache entry.
  const bytes = new Uint8Array(buffer.slice(0));
  if (generation === cacheGeneration) {
    void storeEntry(scope, identity, buffer, generation).catch(() => {
      // Browser storage is an optional acceleration. Quota/private-mode errors
      // must never prevent the authenticated drawing from opening.
    });
  }
  return bytes;
}

export function loadDrawingPdfBytes(request: DrawingPdfCacheRequest): Promise<Uint8Array> {
  const key = cacheKey(request.scope, request.identity);
  if (!request.forceRefresh) {
    const inFlight = inFlightLoads.get(key);
    if (inFlight) return inFlight;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (request.signal?.aborted) controller.abort();
  else request.signal?.addEventListener('abort', abort, { once: true });
  inFlightAbortControllers.set(key, controller);
  const promise = downloadPdf({ ...request, signal: controller.signal }).finally(() => {
    request.signal?.removeEventListener('abort', abort);
    if (inFlightLoads.get(key) === promise) inFlightLoads.delete(key);
    if (inFlightAbortControllers.get(key) === controller) inFlightAbortControllers.delete(key);
  });
  inFlightLoads.set(key, promise);
  return promise;
}

export async function readCachedDrawingPdfBytes(
  request: Pick<DrawingPdfCacheRequest, 'scope' | 'identity' | 'forceRefresh'>,
): Promise<Uint8Array | null> {
  if (request.forceRefresh) {
    await deleteEntry(cacheKey(request.scope, request.identity)).catch(() => {});
    return null;
  }
  return readEntry(request.scope, request.identity).catch(() => null);
}

export async function prefetchDrawingPdf(request: Omit<DrawingPdfCacheRequest, 'forceRefresh'>): Promise<void> {
  if (request.expectedSize && request.expectedSize > DRAWING_PDF_CACHE_MAX_FILE_BYTES) return;
  await loadDrawingPdfBytes(request);
}

export function canPersistDrawingPdf(fileSize: number): boolean {
  return Number.isFinite(fileSize) && fileSize > 0 && fileSize <= DRAWING_PDF_CACHE_MAX_FILE_BYTES;
}

export function shouldWarmDrawingPdf(fileSize: number, constrained: boolean, connection?: {
  saveData?: boolean; effectiveType?: string; downlink?: number;
}): boolean {
  if (!canPersistDrawingPdf(fileSize) || connection?.saveData) return false;
  if (connection?.effectiveType && connection.effectiveType !== '4g') return false;
  if (connection?.downlink != null && connection.downlink < 5) return false;
  const limit = constrained ? 2 * 1024 * 1024 : connection?.effectiveType === '4g' ? 32 * 1024 * 1024 : 8 * 1024 * 1024;
  return fileSize <= limit;
}

// Reuse PDF.js's transport and the ranges it already owns. Never open a
// parallel full-file HTTP download simply to populate an optional cache.
export async function cacheRenderedDrawingPdf(scope: string, identity: string, bytes: Uint8Array, generation: number): Promise<void> {
  if (generation !== cacheGeneration || !canPersistDrawingPdf(bytes.byteLength)) return;
  await storeEntry(scope, identity, bytes.slice().buffer, generation);
}

export function drawingPdfCacheGeneration(): number { return cacheGeneration; }

async function clearDrawingPdfCacheInternal(broadcast: boolean): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  if (broadcast) ensureCacheChannel()?.postMessage({ type: 'clear' });
  cacheGeneration += 1;
  inFlightAbortControllers.forEach((controller) => controller.abort());
  inFlightAbortControllers.clear();
  inFlightLoads.clear();
  const database = await openDatabase().catch(() => null);
  if (database) {
    const transaction = database.transaction(DRAWING_PDF_CACHE_STORE, 'readwrite');
    transaction.objectStore(DRAWING_PDF_CACHE_STORE).clear();
    await transactionComplete(transaction).catch(() => {});
    database.close();
    databasePromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const request = window.indexedDB.deleteDatabase(DRAWING_PDF_CACHE_DB);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error('Drawing cache deletion failed'));
    request.onblocked = () => reject(new Error('Drawing cache deletion is blocked by another tab'));
  });
}

export async function clearDrawingPdfCache(): Promise<void> {
  await clearDrawingPdfCacheInternal(true);
}
